import { z } from 'zod';

const priceDecimal = z.string().regex(/^-?\d+(\.\d{1,4})?$/);
const qtyDecimal = z.string().regex(/^\d+(\.\d{1,4})?$/);

/**
 * A selectable product variant/option (e.g. "Red / A4"). Each variant is its own
 * sellable choice with an own barcode, own sale price (null ⇒ item base price),
 * and an own stock quantity (null ⇒ not tracked). The base item itself is the
 * default; variants are the alternatives a customer picks on the storefront.
 */
export const ItemVariantSchema = z.object({
  id: z.string().uuid(),
  itemId: z.string().uuid(),
  name: z.string().min(1).max(128),
  // Arabic display label (null ⇒ fall back to `name`). Shown on Arabic storefronts.
  nameAr: z.string().max(128).nullable(),
  // English display label (null ⇒ fall back to `name`) — the mirror of `nameAr`,
  // for a shop whose own working language (and so `name`) is NOT English: shown
  // only on an English-browsing storefront/app, never on invoices/POS/quotes.
  nameEn: z.string().max(128).nullable(),
  barcode: z.string().max(64).nullable(),
  salePrice: priceDecimal.nullable(),
  stockQty: qtyDecimal.nullable(),
  sortOrder: z.number().int(),
  isActive: z.boolean(),
});
export type ItemVariant = z.infer<typeof ItemVariantSchema>;

export const ItemVariantInputSchema = z.object({
  name: z.string().min(1).max(128),
  nameAr: z.string().max(128).optional(),
  nameEn: z.string().max(128).optional(),
  barcode: z.string().max(64).optional(),
  salePrice: priceDecimal.optional(),
  stockQty: qtyDecimal.optional(),
});
export type ItemVariantInput = z.infer<typeof ItemVariantInputSchema>;
